// src/pages/Projects.js
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';
import { can } from '../utils/permissions';
import { getCurrentUser } from '../utils/auth';
import ProjectForm from '../components/ProjectForm';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import {
  CheckCircleIcon, PauseIcon, SearchIcon, BuildingIcon, MapPinIcon,
  ChevronUpIcon, ChevronDownIcon, EditIcon, PlusIcon, AlertTriangleIcon, ClockIcon,
} from '../components/Icon';

const TYPE_COLORS = {
  Apartment: '#2980b9',
  Villa:     '#27ae60',
  Plot:      '#b8862e',
  Commercial:'#8e44ad',
  Farmhouse: '#c0392b',
};

const TYPES = ['Apartment','Villa','Plot','Commercial','Farmhouse'];

// Colour map for the project-stage badge (Ongoing / Upcoming / Completed / EOI).
const STATUS_COLORS = {
  ongoing:   { bg: '#e8f6ef', fg: '#1e8449', bd: '#a9dfbf' },
  upcoming:  { bg: '#fef5e7', fg: '#b9770e', bd: '#f5cba7' },
  completed: { bg: '#eaf2f8', fg: '#21618c', bd: '#aed6f1' },
  eoi:       { bg: '#f5eafe', fg: '#7d3cb8', bd: '#d9b8f0' },
};

export default function Projects() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [search, setSearch]   = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tab, setTab]         = useState('active'); // 'active' | 'inactive' | 'notstarted'
  const [editing, setEditing] = useState(null);  // null | 'new' | project object
  const [expanded, setExpanded] = useState(null);
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const currentUser = getCurrentUser();
  const canCreate = can(currentUser, 'projects.create');
  const canEdit   = can(currentUser, 'projects.edit');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.projects();
      setRows(res.projects || []);
      setError(false);
    } catch {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (p) => {
    const next = p.is_active === '1' || p.is_active === 1 ? 0 : 1;
    await api.projectToggle(p.id, next);
    load();
  };

  const handleSaved = () => { setEditing(null); load(); };

  const isActive = (r) => r.is_active === '1' || r.is_active === 1;
  // "Not started" = the project's EOI has not opened yet (status Upcoming).
  const isNotStarted = (r) => String(r.project_status || '').toLowerCase() === 'upcoming';
  const activeCount     = rows.filter(isActive).length;
  const inactiveCount   = rows.length - activeCount;
  const notStartedCount = rows.filter(isNotStarted).length;

  const filtered = rows.filter(r => {
    if (tab === 'active'     && !isActive(r)) return false;
    if (tab === 'inactive'   &&  isActive(r)) return false;
    if (tab === 'notstarted' && !isNotStarted(r)) return false;
    if (typeFilter && r.project_type !== typeFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!r.code_name?.toLowerCase().includes(s) &&
          !r.location?.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const pageStart = (page - 1) * pageSize;
  const paged     = filtered.slice(pageStart, pageStart + pageSize);

  // Reset to page 1 whenever filters/tab change
  useEffect(() => { setPage(1); }, [tab, typeFilter, search, pageSize]);

  const columns = [
    { key: 'code_name', header: 'Code Name', nowrap: true, render: (p) => (
      <div className="proj-code">
        {p.code_name}
        {p.prefix && <span className="proj-prefix" title={`EOI number format: EOI-${p.prefix}-0001`}>{p.prefix}</span>}
      </div>
    ) },
    { key: 'location', header: 'Location', tdStyle: { fontSize: 12, color: '#666' }, render: (p) => (
      <span className="proj-loc"><MapPinIcon size={11} color="#aaa" />{p.location}</span>
    ) },
    { key: 'project_type', header: 'Type', render: (p) => (
      <span className={`pill pill--${(p.project_type || '').toLowerCase() || 'default'}`}>
        {p.project_type}
      </span>
    ) },
    { key: 'price_range', header: 'Price Range', nowrap: true, tdStyle: { fontSize: 12, color: '#b8862e', fontWeight: 700 }, render: (p) => p.price_range },
    { key: 'project_status', header: 'Project Status', align: 'center', render: (p) => {
      const st = String(p.project_status || '').trim();
      if (!st) return <span className="tbl-dash">—</span>;
      const c = STATUS_COLORS[st.toLowerCase()] || { bg: '#f0f0f0', fg: '#666', bd: '#ddd' };
      return (
        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
          {st}
        </span>
      );
    } },
    { key: 'status', header: 'Active', align: 'center', render: (p) => {
      const active = isActive(p);
      return (
        <label className={`toggle${active ? ' is-on' : ''}`} title={canEdit ? (active ? 'Click to deactivate' : 'Click to activate') : 'No permission'}>
          <input type="checkbox" className="toggle-input" checked={active} disabled={!canEdit} onChange={() => handleToggle(p)} />
          <span className="toggle-dot" />
        </label>
      );
    } },
    { key: 'actions', header: 'Actions', nowrap: true, align: 'right', render: (p) => {
      const isOpen = expanded === p.id;
      return (
        <div className="proj-actions">
          <button className="proj-link-btn" onClick={() => setExpanded(isOpen ? null : p.id)}>
            {isOpen ? <><ChevronUpIcon size={12} /> Less</> : <><ChevronDownIcon size={12} /> More</>}
          </button>
          {canEdit && (
            <button className="icon-btn icon-btn--edit" onClick={() => setEditing(p)} title="Edit" aria-label="Edit"><EditIcon size={13} /></button>
          )}
          
        </div>
      );
    } },
  ];

  const renderExpanded = (p) => {
    if (expanded !== p.id) return null;
    return (
      <div className="proj-expand-grid">
        <Detail label="BHK" value={p.bhk_options_arr?.length > 0 ? p.bhk_options_arr.join(', ') : '—'} />
        <Detail label="Areas (sq.ft.)" value={p.unit_areas_arr?.length > 0 ? p.unit_areas_arr.join(', ') : '—'} />
        {p.floor_options_arr?.length > 0 && (
          <Detail label="Floor Options" value={p.floor_options_arr.join(' · ')} />
        )}
        {p.facings_arr?.length > 0 && (
          <Detail label="Facings" value={p.facings_arr.join(' · ')} />
        )}
        <Detail label="Price Range (₹)" value={`${Number(p.price_min).toLocaleString('en-IN')} – ${Number(p.price_max).toLocaleString('en-IN')}`} />
        <Detail label="Sort Order" value={p.sort_order} />
        <Detail label="Created" value={p.created_at} />
      </div>
    );
  };

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <div className="page-sub">{rows.length} total · {activeCount} active · {inactiveCount} inactive</div>
        </div>
        {canCreate && (
          <button className="btn-primary" onClick={() => setEditing('new')}>
            <PlusIcon size={14} /> <span>Add Project</span>
          </button>
        )}
      </div>

      {/* Tabs (left) + Search/Type (right) */}
      <div className="toolbar proj-toolbar">
        <div className="tabs">
          <button
            className={`tab${tab === 'active' ? ' is-active' : ''}`}
            onClick={() => setTab('active')}
          >
            <CheckCircleIcon size={14} /> Active <span className="tab-count">{activeCount}</span>
          </button>
          <button
            className={`tab${tab === 'inactive' ? ' is-active' : ''}`}
            onClick={() => setTab('inactive')}
          >
            <PauseIcon size={14} /> Inactive <span className="tab-count">{inactiveCount}</span>
          </button>
          <button
            className={`tab${tab === 'notstarted' ? ' is-active' : ''}`}
            onClick={() => setTab('notstarted')}
          >
            <ClockIcon size={14} /> Not Started <span className="tab-count">{notStartedCount}</span>
          </button>
        </div>

        <div className="proj-right">
          <div className="proj-search-wrap">
            <SearchIcon size={14} className="search-icon" />
            <input
              className="search-input"
              placeholder="Search by code or location..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select className="proj-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {(search || typeFilter) && (
            <button className="reset-btn" onClick={() => { setSearch(''); setTypeFilter(''); }}>Reset</button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="table-card">
        {loading ? (
          <div className="loader-wrap"><div className="spinner" /></div>
        ) : error ? (
          <div className="empty-state">
            <div><AlertTriangleIcon size={36} color="#c0392b" /></div>
            <div className="proj-err-title">Cannot load projects</div>
            <div className="proj-err-sub">Check the API connection.</div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={paged}
            rowKey={(p) => p.id}
            minWidth={760}
            maxHeight="60vh"
            rowStyle={(p) => (isActive(p) ? null : { opacity: 0.55 })}
            renderExpanded={renderExpanded}
            emptyMessage={
              <div>
                <div className="empty-icon-muted"><BuildingIcon size={40} /></div>
                <div className="empty-sub">No projects found</div>
              </div>
            }
          />
        )}

        {/* Pagination — kept inside the same card as the table (like the Dashboard) */}
        {!loading && !error && filtered.length > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {/* Form modal */}
      {editing && (
        <ProjectForm
          project={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="detail-label">{label}</div>
      <div className="detail-val">{value}</div>
    </div>
  );
}
